import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  InputAdornment,
  Autocomplete
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  MyLocation as MyLocationIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Map as MapIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { RealMapComponent } from './RealMapComponent';
import { reverseGeocode, searchPlaces, POPULAR_LOCATIONS } from '../../services/geocoding';
import type { Coordinates, GeoLocation, MapMarker } from '../../types';

interface LocationPickerProps {
  value?: GeoLocation | null;
  onChange: (location: GeoLocation | null) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  label?: string;
  placeholder?: string;
}

// Используем POPULAR_LOCATIONS из сервиса геокодирования
const POPULAR_PLACES = POPULAR_LOCATIONS;

export const LocationPicker: React.FC<LocationPickerProps> = ({
  value,
  onChange,
  disabled = false,
  error = false,
  helperText,
  label = "Местоположение",
  placeholder = "Выберите координаты на карте"
}) => {
  const { t } = useTranslation();
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCoordinates, setSelectedCoordinates] = useState<Coordinates | null>(
    value ? { latitude: value.latitude, longitude: value.longitude } : null
  );
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodingError, setGeocodingError] = useState<string | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<typeof POPULAR_PLACES[0] | null>(null);
  
  // Форматирование координат для отображения
  const formatCoordinates = (coords: Coordinates): string => {
    return `${coords.latitude.toFixed(6)}, ${coords.longitude.toFixed(6)}`;
  };
  
  // Отображение адреса
  const getDisplayText = (): string => {
    if (!value) return '';
    
    if (value.full_address) {
      return value.full_address;
    }
    
    if (value.city && value.country) {
      return `${value.city}, ${value.country}`;
    }
    
    return formatCoordinates({ latitude: value.latitude, longitude: value.longitude });
  };

  // Обработка клика по карте
  const handleMapClick = useCallback(async (coordinates: Coordinates) => {
    setSelectedCoordinates(coordinates);
    setIsGeocoding(true);
    setGeocodingError(null);
    
    try {
      const location = await reverseGeocode(coordinates);
      onChange(location);
    } catch (error) {
      console.error('Ошибка геокодирования:', error);
      setGeocodingError('Не удалось определить адрес по координатам');
      
      // Fallback: создаем локацию с координатами
      const fallbackLocation: GeoLocation = {
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        country: undefined,
        city: undefined,
        full_address: `${coordinates.latitude.toFixed(6)}, ${coordinates.longitude.toFixed(6)}`,
        location_id: undefined
      };
      onChange(fallbackLocation);
    } finally {
      setIsGeocoding(false);
    }
  }, [onChange]);

  // Открытие диалога выбора
  const handleOpenDialog = () => {
    if (disabled) return;
    setIsDialogOpen(true);
  };

  // Закрытие диалога
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setGeocodingError(null);
  };

  // Очистка значения
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setSelectedCoordinates(null);
  };

  // Ручной ввод координат
  const handleManualInput = (field: 'latitude' | 'longitude', stringValue: string) => {
    const numValue = parseFloat(stringValue);
    if (isNaN(numValue)) return;
    
    const coords = selectedCoordinates || { latitude: 0, longitude: 0 };
    const newCoords = { ...coords, [field]: numValue };
    
    // Валидация диапазонов
    if (field === 'latitude' && (numValue < -90 || numValue > 90)) return;
    if (field === 'longitude' && (numValue < -180 || numValue > 180)) return;
    
    setSelectedCoordinates(newCoords);
  };

  // Применение ручного ввода
  const handleApplyManualInput = async () => {
    if (!selectedCoordinates) return;
    
    await handleMapClick(selectedCoordinates);
  };

  // Обработка выбора популярного места
  const handlePlaceSelect = async (place: typeof POPULAR_PLACES[0] | null) => {
    if (!place) return;
    
    setSelectedPlace(place);
    setSelectedCoordinates({ latitude: place.latitude, longitude: place.longitude });
    
    // Автоматически применяем координаты
    const location: GeoLocation = {
      latitude: place.latitude,
      longitude: place.longitude,
      country: place.country,
      city: place.city,
      full_address: place.name,
      location_id: undefined
    };
    
    onChange(location);
  };

  // Маркеры для карты
  const markers: MapMarker[] = selectedCoordinates ? [{
    id: 1,
    name: 'Выбранная точка',
    coordinates: selectedCoordinates,
    tooltip: formatCoordinates(selectedCoordinates)
  }] : [];

  return (
    <>
      {/* Поле ввода */}
      <TextField
        fullWidth
        label={label}
        placeholder={placeholder}
        value={getDisplayText()}
        onClick={handleOpenDialog}
        error={error}
        helperText={helperText}
        disabled={disabled}
        InputProps={{
          readOnly: true,
          startAdornment: (
            <InputAdornment position="start">
              <LocationIcon color={value ? 'primary' : 'disabled'} />
            </InputAdornment>
          ),
          endAdornment: value && !disabled && (
            <InputAdornment position="end">
              <Tooltip title={t('common.clear')}>
                <IconButton size="small" onClick={handleClear}>
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
          style: { cursor: disabled ? 'default' : 'pointer' }
        }}
      />

      {/* Диалог выбора координат */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '80vh', maxHeight: 800 }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <MapIcon />
            {t('map.selectLocation')}
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Поиск популярных мест */}
            <Paper elevation={0} sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" gutterBottom>
                <SearchIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 16 }} />
                {t('map.popularPlaces')}
              </Typography>
              
              <Autocomplete
                size="small"
                options={POPULAR_PLACES}
                getOptionLabel={(option) => option.name}
                value={selectedPlace}
                onChange={(_, newValue) => handlePlaceSelect(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t('map.searchPlaceholder')}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                noOptionsText={t('map.noPlacesFound')}
              />
            </Paper>

            {/* Панель ручного ввода */}
            <Paper elevation={0} sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" gutterBottom>
                <EditIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 16 }} />
                {t('map.manualInput')}
              </Typography>
              
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    label={t('map.latitude')}
                    type="number"
                    size="small"
                    fullWidth
                    value={selectedCoordinates?.latitude || ''}
                    onChange={(e) => handleManualInput('latitude', e.target.value)}
                    inputProps={{
                      step: 0.000001,
                      min: -90,
                      max: 90
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    label={t('map.longitude')}
                    type="number"
                    size="small"
                    fullWidth
                    value={selectedCoordinates?.longitude || ''}
                    onChange={(e) => handleManualInput('longitude', e.target.value)}
                    inputProps={{
                      step: 0.000001,
                      min: -180,
                      max: 180
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleApplyManualInput}
                    disabled={!selectedCoordinates || isGeocoding}
                    startIcon={<EditIcon />}
                  >
                    {t('map.applyCoordinates')}
                  </Button>
                </Grid>
              </Grid>
              
              {isGeocoding && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {t('map.geocoding')}
                </Alert>
              )}
              
              {geocodingError && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {geocodingError}
                </Alert>
              )}
            </Paper>

            {/* Карта */}
            <Box sx={{ flex: 1, position: 'relative' }}>
              <RealMapComponent
                markers={markers}
                onMapClick={handleMapClick}
                selectedMarker={markers[0] || null}
                showControls={true}
                interactive={true}
                config={selectedCoordinates ? {
                  center: selectedCoordinates,
                  zoom: 15
                } : undefined}
              />
              
              {/* Инструкция */}
              {!selectedCoordinates && (
                <Paper
                  elevation={2}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    p: 3,
                    textAlign: 'center',
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(4px)',
                    pointerEvents: 'none'
                  }}
                >
                  <LocationIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    {t('map.clickToSelect')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('map.clickInstruction')}
                  </Typography>
                </Paper>
              )}
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            {t('common.cancel')}
          </Button>
          
          <Button 
            variant="contained" 
            onClick={handleCloseDialog}
            disabled={!selectedCoordinates}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}; 